/**
 * Username QR rendering policy.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/UsernameQRRenderingPolicy.swift`.
 *
 * Pure-function helper that hands the QR renderer two strings: the
 * universal-link form (preferred — taps a https URL that browsers and apps
 * route through universal-link entitlements) and the custom-scheme form
 * (fallback — used when the receiving device hasn't installed the app yet,
 * or when sharing over messengers that mangle https URLs).
 *
 * The display handle is the formatted username. Renderers must NOT surface
 * the fingerprint string to the user — it is a verification hint, not a
 * human-meaningful identifier.
 *
 * No SolidJS imports. No QR rendering. No scanner. Pure logic only.
 */

import {
  type UsernameShareLink,
  encodeUniversalLink,
  encodeCustomScheme,
  formatHandle,
} from "./UsernameShareLink";

// ---------------------------------------------------------------------------
// View-model type
// ---------------------------------------------------------------------------

/**
 * Mirrors iOS `UsernameQRViewModel`.
 *
 * `encodedString`  — universal-link form; feed this into a QR generator as
 *                    the primary payload.
 * `fallbackString` — custom-scheme form; offer as a copy-button / secondary
 *                    QR for devices without the app installed.
 * `displayHandle`  — formatted username ("alice.0042"); render as a caption
 *                    beneath the QR. Never render the fingerprint.
 */
export interface UsernameQRViewModel {
  readonly encodedString: string;
  readonly fallbackString: string;
  readonly displayHandle: string;
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Build the view model. Both encodings are derived from the same
 * `UsernameShareLink` so a renderer that flips between them cannot
 * accidentally surface inconsistent identity material.
 *
 * Mirrors iOS `UsernameQRRenderingPolicy.buildViewModel`.
 */
export function buildViewModel(link: UsernameShareLink): UsernameQRViewModel {
  return {
    encodedString: encodeUniversalLink(link),
    fallbackString: encodeCustomScheme(link),
    displayHandle: formatHandle(link.handle),
  };
}
